document.addEventListener('DOMContentLoaded', function() {
  /**
   * Polls for an element with retry logic
   * @param {string} selector - CSS selector for the element
   * @param {number} maxAttempts - Maximum number of attempts
   * @param {number} delay - Delay between attempts in milliseconds
   * @returns {Promise<Element|null>} - The element if found, null otherwise
   */
  function pollForElement(selector, maxAttempts = 5, delay = 500) {
    return new Promise((resolve) => {
      let attempts = 0;

      const checkElement = () => {
        attempts++;
        const element = document.querySelector(selector);

        if (element) {
          resolve(element);
          return;
        }

        if (attempts >= maxAttempts) {
          resolve(null);
          return;
        }

        setTimeout(checkElement, delay);
      };

      checkElement();
    });
  }

  /**
   * Gets the CSS property value from inline style attribute
   * @param {Element} element - The element
   * @param {string} property - CSS property name
   * @returns {string|null} - The property value or null
   */
  function getInlineStyleValue(element, property) {
    if (!element || !element.style) {
      return null;
    }

    const styleAttr = element.getAttribute('style');
    if (!styleAttr) {
      return null;
    }

    // Parse inline styles
    const styleObj = {};
    styleAttr.split(';').forEach(rule => {
      const [key, value] = rule.split(':').map(s => s.trim());
      if (key && value) {
        styleObj[key.replace(/-([a-z])/g, (g) => g[1].toUpperCase())] = value;
      }
    });

    const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    return styleObj[camelProperty] || null;
  }

  /**
   * Determines size fit tier based on percentage
   * Ranges: 0-25% = Very Small, 25-45% = Small, 45-55% = Perfect, 55-75% = Large, 75-100% = Very Large
   * @param {number} percentage - Percentage value (0-100)
   * @returns {string|null} - Tier key or null
   */
  function getSizeFitTier(percentage) {
    if (percentage >= 0 && percentage <= 25) {
      return 'verySmall';
    } else if (percentage > 25 && percentage <= 45) {
      return 'small';
    } else if (percentage > 45 && percentage <= 55) {
      return 'perfect';
    } else if (percentage > 55 && percentage <= 75) {
      return 'large';
    } else if (percentage > 75 && percentage <= 100) {
      return 'veryLarge';
    }
    return null;
  }

  /**
   * Main function to process Judge.me size fit
   */
  async function processJudgeMeSizeFit() {
    // debugger;

    // Poll for the Judge.me element
    const judgeMeWidget = await pollForElement('.jdgm-rev-widg', 5, 500);
    /*
    if (judgeMeWidget && parseInt(judgeMeWidget.dataset.numberOfReviews) === 0) {
      judgeMeWidget.closest('.shopify-section').remove();
    }
*/
    const judgeMeElement = await pollForElement('.jdgm-custom-forms-avg-responses .jdgm-rev__cf-ans', 5, 500);
    
    if (!judgeMeElement) {
      return; // Element not found after max attempts
    }

    // Check if element has display: none in style attribute
    const styleAttr = judgeMeElement.getAttribute('style');
    if (styleAttr && styleAttr.includes('display: none')) {
      return; // Element is hidden, do nothing
    }

    // Find the slider pointer inside
    const sliderPointer = judgeMeElement.querySelector('.jdgm-rev__slider-pointer');
    if (!sliderPointer) {
      return; // Slider pointer not found
    }

    // Get the left CSS value
    const leftValue = getInlineStyleValue(sliderPointer, 'left');
    if (!leftValue) {
      return; // No left style attribute
    }

    // Extract percentage value
    const percentageMatch = leftValue.match(/(\d+(?:\.\d+)?)%/);
    if (!percentageMatch) {
      return; // Invalid percentage format
    }

    const percentage = parseFloat(percentageMatch[1]);
    const tierKey = getSizeFitTier(percentage);

    if (!tierKey || !theme || !theme.strings || !theme.strings.sizeFitTiers) {
      return; // Tier not found or translations not available
    }

    const tierText = theme.strings.sizeFitTiers[tierKey];
    if (!tierText) {
      return; // Translation not found
    }

    // Find all .judge-me-size-fit elements and populate them
    const sizeFitElements = document.querySelectorAll('.judge-me-size-fit');
    sizeFitElements.forEach(element => {
      if (element.textContent.trim() === '') {
        element.textContent = tierText;
        element.parentElement.style.display = 'block';
      }
    });
  }

  // Run the process
  processJudgeMeSizeFit();

  /**
   * Handle store availability button click
   */
  function initStoreAvailabilityButton() {
    const storeButton = document.querySelector('[data-store-availability-trigger]');
    
    if (!storeButton) {
      return; // Button not found
    }

    storeButton.addEventListener('click', function() {
      // Try to find the inventory info app block
      const inventoryApp = document.querySelector('#inventory-info-app');
      
      if (!inventoryApp) {
        return; // App block doesn't exist
      }

      // Try to find the clickable element within the app
      const clickableElement = inventoryApp.querySelector('.iia-header');
      
      if (!clickableElement) {
        return; // Clickable element not found
      }

      // Check if the collapsible is closed (no .iia-chevron-up class)
      const chevron = inventoryApp.querySelector('.iia-chevron');
      const isCollapsibleClosed = chevron && !chevron.classList.contains('iia-chevron-up');

      // Helper function to scroll only the main window, not intermediate overflow containers
      const scrollToElement = (element) => {
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const headerOffset = 0; // Adjust if you have a fixed header
        
        const topInViewport = rect.top >= 0 && rect.top < viewportHeight;
        const bottomInViewport = rect.bottom > 0 && rect.bottom <= viewportHeight;
        const elementHeight = rect.height;
        
        // If both top and bottom edges are visible, do nothing
        if (topInViewport && bottomInViewport) {
          return;
        }
        
        const absoluteTop = rect.top + window.pageYOffset;
        let scrollTarget;
        
        // If element is larger than viewport, scroll to align top with viewport top
        if (elementHeight > viewportHeight) {
          scrollTarget = absoluteTop - headerOffset;
        }
        // If top edge is not in viewport, scroll to show both edges (or top if too large)
        else if (!topInViewport) {
          scrollTarget = absoluteTop - headerOffset;
        }
        // If top is visible but bottom is not, scroll down to show both edges
        else if (topInViewport && !bottomInViewport) {
          // Calculate how much to scroll to show the bottom edge
          const bottomAbsolute = rect.bottom + window.pageYOffset;
          scrollTarget = bottomAbsolute - viewportHeight;
        }
        
        window.scrollTo({
          top: scrollTarget,
          behavior: 'smooth'
        });
      };

      if (isCollapsibleClosed) {
        // If closed: click first, wait 500ms, then scroll
        clickableElement.click();
        
        setTimeout(() => {
          // Check viewport position of the container, not the clickable element
          scrollToElement(inventoryApp);
        }, 500);
      } else {
        // If already open: just scroll to the container
        scrollToElement(inventoryApp);
      }
    });
  }

  // Initialize store availability button
  initStoreAvailabilityButton();

  /**
   * Complementary Products Slider Custom Element
   */
  class ComplementaryProductsSlider extends HTMLElement {
    constructor() {
      super();
      this.productId = this.dataset.productId;
      this.limit = parseInt(this.dataset.limit) || 6;
      this.slider = null;
      this.prevButton = null;
      this.nextButton = null;
      this.shopTheLookButton = document.querySelector('[data-scroll-to-complementary]');
    }

    connectedCallback() {
      this.fetchRecommendations();
      this.initShopTheLookButton();
    }

    async fetchRecommendations() {
      try {
        // Fetch the section with complementary product recommendations
        const url = `${window.Shopify.routes.root}recommendations/products?section_id=complementary-products&limit=${this.limit}&product_id=${this.productId}&intent=complementary`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error('Failed to fetch recommendations');
        }

        const html = await response.text();
        
        // Parse the HTML response
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const section = doc.querySelector('.complementary-products-section');
        
        if (section) {
          // Replace loading content with the rendered section
          this.innerHTML = '';
          this.appendChild(section);

          new theme.QuickAdd(this);
          new theme.QuickShop(this);

          this.closest('.product-block--complementary').classList.add('loaded');
          
          // Get product count
          const productCount = parseInt(section.dataset.productCount) || 0;
          
          // Initialize slider functionality
          this.slider = this.querySelector('.complementary-products__slider');
          this.prevButton = this.querySelector('.complementary-products__arrow--prev');
          this.nextButton = this.querySelector('.complementary-products__arrow--next');
          
          // Only initialize slider if 3+ products
          if (productCount >= 3 && this.slider && this.prevButton && this.nextButton) {
            this.initSlider();
          }

          // Show "Shop the Look" button if products loaded
          if (productCount > 0 && this.shopTheLookButton) {
            this.shopTheLookButton.classList.add('is-visible');
          }

          // Reinitialize lazy loading for images
          if (window.lazySizes) {
            window.lazySizes.init();
          }

          // Reinitialize product grid items if theme has this function
          if (window.theme && window.theme.reinitProductGridItem) {
            window.theme.reinitProductGridItem(this);
          }
        } else {
          // No products found, hide/remove the button
          if (this.shopTheLookButton) {
            this.shopTheLookButton.remove();
          }
        }
      } catch (error) {
        console.error('Error fetching complementary product recommendations:', error);
        // On error, remove the button
        if (this.shopTheLookButton) {
          this.shopTheLookButton.remove();
        }
      }
    }

    initShopTheLookButton() {
      if (!this.shopTheLookButton) return;

      this.shopTheLookButton.addEventListener('click', () => {
        // Scroll to this complementary products block
        this.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
    }

    initSlider() {
      // Arrow click handlers
      this.prevButton.addEventListener('click', () => this.scrollPrev());
      this.nextButton.addEventListener('click', () => this.scrollNext());

      // Update buttons on scroll
      this.slider.addEventListener('scroll', () => {
        this.updateButtonStates();
      });

      // Set initial button states (use setTimeout to ensure layout is complete)
      setTimeout(() => {
        this.updateButtonStates();
      }, 100);
    }

    scrollPrev() {
      const slideWidth = this.slider.querySelector('.complementary-products__slide')?.offsetWidth || 0;
      const gap = 15;
      this.slider.scrollBy({
        left: -(slideWidth + gap),
        behavior: 'smooth'
      });
    }

    scrollNext() {
      const slideWidth = this.slider.querySelector('.complementary-products__slide')?.offsetWidth || 0;
      const gap = 15;
      this.slider.scrollBy({
        left: slideWidth + gap,
        behavior: 'smooth'
      });
    }

    updateButtonStates() {
      if (!this.slider) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.slider;
      const isAtStart = scrollLeft <= 0;
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      this.prevButton.disabled = isAtStart;
      this.nextButton.disabled = isAtEnd;
    }
  }

  // Register the custom element
  if (!customElements.get('complementary-products-slider')) {
    customElements.define('complementary-products-slider', ComplementaryProductsSlider);
  }

  /**
   * Sticky Add to Cart Sync
   */
  function initStickyAddToCartSync() {
    const stickySelect = document.querySelector('[data-sticky-variant-select]');
    const stickyButton = document.querySelector('[data-sticky-add-to-cart]');
    const stickyButtonText = document.querySelector('[data-sticky-button-text]');
    const productContainer = document.querySelector('[data-section-type="product"]');

    if (!stickySelect || !stickyButton || !productContainer) {
      return; // Required elements not found
    }

    const sizeOptionIndex = stickySelect.dataset.optionIndex;

    // Handle sticky button clicks by triggering the main form's add to cart button
    stickyButton.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      // Find the main product form's add to cart button
      const mainAddToCartButton = productContainer.querySelector('button[name="add"], button[type="submit"].add-to-cart');
      
      if (mainAddToCartButton && !mainAddToCartButton.disabled) {
        // Trigger click on the main button so Rebuy can intercept it
        mainAddToCartButton.click();
      }
    });

    // Helper function to get variant metafield data
    function getVariantMetafield(variantId) {
      const metafieldsEl = productContainer.querySelector('[data-variant-metafields]');
      if (!metafieldsEl) return null;
      
      try {
        const metafieldsData = JSON.parse(metafieldsEl.innerHTML);
        return metafieldsData[variantId] || null;
      } catch (e) {
        return null;
      }
    }

    // Sync sticky select change to main product form
    stickySelect.addEventListener('change', function() {
      const selectedValue = this.value;
      
      // Find and trigger the corresponding radio button on the main form
      const radioButton = productContainer.querySelector(
        `input[data-variant-input][data-option-index="${sizeOptionIndex}"][value="${selectedValue}"]`
      );
      
      if (radioButton && !radioButton.classList.contains('disabled')) {
        radioButton.checked = true;
        radioButton.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Helper function to update sticky select options availability
    function updateStickySelectOptions() {
      const mainSizeInputs = productContainer.querySelectorAll(
        `input[data-variant-input][data-option-index="${sizeOptionIndex}"]`
      );
      
      // Update each option in sticky select based on main form state
      Array.from(stickySelect.options).forEach(option => {
        const mainInput = Array.from(mainSizeInputs).find(
          input => input.value === option.value
        );
        
        if (mainInput && mainInput.classList.contains('disabled')) {
          option.disabled = true;
        } else {
          option.disabled = false;
        }
      });
    }

    updateStickySelectOptions();

    // Listen for variant changes from the main product form
    productContainer.addEventListener('variantChange', function(evt) {
      const variant = evt.detail.variant;
      
      if (!variant) {
        // No variant selected
        stickyButton.disabled = true;
        stickyButton.classList.add('disabled');
        if (stickyButtonText) {
          const defaultText = stickyButtonText.dataset.defaultText;
          stickyButtonText.textContent = window.theme?.strings?.unavailable || 'Unavailable';
        }
        return;
      }

      // Get variant metafield to check variant_oos
      const variantMetafield = getVariantMetafield(variant.id);
      const isVariantOOS = variantMetafield?.variant_oos === true;

      // Update sticky select to match current variant
      if (variant.options && variant.options[sizeOptionIndex]) {
        stickySelect.value = variant.options[sizeOptionIndex];
      }

      // Update sticky select options availability
      updateStickySelectOptions();

      // Update button state and text
      const isAvailable = variant.available && !isVariantOOS;
      
      if (isAvailable) {
        stickyButton.disabled = false;
        stickyButton.classList.remove('disabled');
        if (stickyButtonText) {
          const defaultText = stickyButtonText.dataset.defaultText;
          stickyButtonText.innerHTML = `${defaultText} – ${theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat)}`;
        }
      } else {
        stickyButton.disabled = true;
        stickyButton.classList.add('disabled');
        if (stickyButtonText) {
          stickyButtonText.textContent = window.theme?.strings?.soldOut || 'Sold Out';
        }
      }
    });

    // Trigger initial sync on page load
    // const initialEvent = new CustomEvent('variantChange', {
    //   detail: {
    //     variant: window.theme?.currentVariant || null
    //   }
    // });
    
    // // Wait a bit for theme to initialize
    // setTimeout(() => {
    //   if (productContainer) {
    //     productContainer.dispatchEvent(initialEvent);
    //   }
    // }, 100);
  }

  // Initialize sticky add to cart sync
  initStickyAddToCartSync();





  // Product Info Sticky Content
  const pdpInfo = document.querySelector('.product-grid__content');
  const pdpInfoContent = pdpInfo ? pdpInfo.querySelector('.product-single__meta') : null;

  if (pdpInfo && pdpInfoContent) {
    // Запускаем логику только после полной загрузки страницы
    window.addEventListener('load', function() {
      (function() {
        const imageGallery = document.querySelector('.product-images-wrapper');
        
        // Если не нашли галерею - выходим
        if (!imageGallery) return;
        
        let lastScrollTop = window.pageYOffset;
        let currentTranslateY = 0;
        let rafId = null;
        let isDesktop = window.innerWidth >= 769;

        function updatePosition() {
          // Проверяем размер экрана перед каждым обновлением
          if (!isDesktop) {
            // Сбрасываем transform и класс на мобильных
            pdpInfoContent.style.transform = '';
            pdpInfo.classList.remove('product-grid__content--limited');
            return;
          }

          // Проверяем высоты каждый фрейм (высота контента может меняться из-за коллапсиблов)
          const galleryHeight = imageGallery.offsetHeight;
          const contentHeight = pdpInfoContent.offsetHeight;
          const shouldLimit = galleryHeight > contentHeight;

          if (!shouldLimit) {
            // Если контент выше галереи - убираем ограничение и transform
            pdpInfo.classList.remove('product-grid__content--limited');
            pdpInfoContent.style.transform = '';
            currentTranslateY = 0;
            lastScrollTop = window.pageYOffset;
            if (isDesktop) {
              rafId = requestAnimationFrame(updatePosition);
            }
            return;
          }

          // Галерея выше контента - добавляем ограничение и работаем с transform
          pdpInfo.classList.add('product-grid__content--limited');

          // const headerHeightStr = getComputedStyle(document.body).getPropertyValue('--header-height');
          // const headerHeight = parseInt(headerHeightStr, 10) || 0;

          const scrollTop = window.pageYOffset;
          const scrollDelta = scrollTop - lastScrollTop;

          // Проверяем, прилип ли pdpInfo к хедеру
          const pdpInfoTop = pdpInfo.getBoundingClientRect().top;
          // const isSticky = Math.abs(pdpInfoTop - headerHeight) < 2;
          const isSticky = Math.abs(pdpInfoTop) < 2;

          // Вычисляем границы для transform
          const pdpInfoContentHeight = pdpInfoContent.offsetHeight;
          const pdpInfoHeight = pdpInfo.offsetHeight;
          const maxTranslate = Math.max(0, pdpInfoContentHeight - pdpInfoHeight);

          // Двигаем контент только когда pdpInfo прилипает к хедеру
          if (isSticky) {
            if (scrollDelta > 0) {
              // Скролл вниз - поднимаем контент вверх
              currentTranslateY = Math.min(currentTranslateY + scrollDelta, maxTranslate);
            } else if (scrollDelta < 0) {
              // Скролл вверх - опускаем контент вниз
              currentTranslateY = Math.max(currentTranslateY + scrollDelta, 0);
            }
          // } else if (pdpInfoTop > headerHeight) {
          } else if (pdpInfoTop > 0) {
            // Сбрасываем transform если еще не доскролили
            currentTranslateY = 0;
          // } else if (pdpInfoTop < headerHeight) {
          } else if (pdpInfoTop < 0) {
            // Секция ушла вверх из viewport - фиксируем maxTranslate
            currentTranslateY = maxTranslate;
          }

          // Применяем transform
          pdpInfoContent.style.transform = `translate3d(0, -${currentTranslateY}px, 0)`;

          lastScrollTop = scrollTop;

          // Постоянный RAF цикл только на desktop
          if (isDesktop) {
            rafId = requestAnimationFrame(updatePosition);
          }
        }

        // Обработчик resize с debounce
        let resizeTimeout;
        window.addEventListener('resize', function() {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(function() {
            const wasDesktop = isDesktop;
            isDesktop = window.innerWidth >= 769;

            // Если изменился режим desktop/mobile
            if (wasDesktop !== isDesktop) {
              if (isDesktop) {
                // Переключились на desktop - запускаем RAF
                currentTranslateY = 0;
                pdpInfoContent.style.transform = '';
                lastScrollTop = window.pageYOffset;
                rafId = requestAnimationFrame(updatePosition);
              } else {
                // Переключились на mobile - останавливаем RAF и сбрасываем
                if (rafId) {
                  cancelAnimationFrame(rafId);
                  rafId = null;
                }
                currentTranslateY = 0;
                pdpInfoContent.style.transform = '';
                pdpInfo.classList.remove('product-grid__content--limited');
              }
            } else if (isDesktop) {
              // Просто resize на desktop - пересчитываем
              currentTranslateY = 0;
              pdpInfoContent.style.transform = '';
              lastScrollTop = window.pageYOffset;
            }
          }, 150);
        });

        // Запускаем RAF цикл только на desktop
        if (isDesktop) {
          rafId = requestAnimationFrame(updatePosition);
        }
      })();
    });
  }



  // Sticky Add to Cart Container
  const addToCartBtn = document.querySelector('#OriginalAddToCart');
  const stickyBtnContainer = document.querySelector('#StickyAddToCartContainer');

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if(entry.isIntersecting) {
        if(stickyBtnContainer.classList.contains('hide')) return
        stickyBtnContainer.classList.add("hide");
        document.body.classList.remove('pdp-sticky-visible');
      } else {
        if (window.scrollY > (addToCartBtn.offsetTop + addToCartBtn.offsetHeight)) {
          stickyBtnContainer.classList.remove("hide");
          document.body.classList.add('pdp-sticky-visible');
        }
      }
    })
  });
  
  observer.observe(addToCartBtn)

  /**
   * Product Share Button - Native Browser Share
   */
  function initProductShareButton() {
    const shareButton = document.querySelector('[data-product-share-button]');
    
    if (!shareButton) {
      return; // Share button not found
    }

    // Check if native share is supported
    if (!navigator.share) {
      // Hide share button if native share is not supported
      shareButton.style.display = 'none';
      return;
    }

    const productContainer = document.querySelector('[data-section-type="product"]');
    let urlToShare = window.location.href;

    // Update share URL when variant changes
    if (productContainer) {
      productContainer.addEventListener('variantChange', function(evt) {
        if (evt.detail && evt.detail.variant) {
          urlToShare = `${window.location.origin}${window.location.pathname}?variant=${evt.detail.variant.id}`;
        }
      });
    }

    // Handle share button click
    shareButton.addEventListener('click', function() {
      navigator.share({
        title: document.title,
        url: urlToShare
      }).catch((error) => {
        // Silently handle user cancellation or errors
        console.log('Share cancelled or error:', error);
      });
    });
  }

  // Initialize product share button
  initProductShareButton();
});